import { isObject, isEmpty } from 'lodash'
import router from '../../router'
import roles from '../../configs/roles'

const isEmailValid = email => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)

export default {
  state: {
    auth: {}, // Auth represents the users Firebase Authentication account
    session: {
      role: null, // Role with respect to the Tenant
      tenant: null, // Firebase Tenant ID
      account: null, // Stripe Connect Account ID
    },
    user: {}, // User represents the User Document in our Firestore
    tenantUser: {},
    isLoggedIn: false,
    reloginDialog: false,
    sessionDialog: false,
  },
  getters: {
    getSessionDialog(state) {
      return state.sessionDialog
    },
    getReloginDialog(state) {
      return state.reloginDialog
    },
    getSessionAccount(state) {
      return state.session.account
    },
    hasStripeAccount(state) {
      return !isEmpty(state.session.account)
    },
    getSessionTenant(state) {
      return state.session.tenant
    },
    getSessionRole(state) {
      return state.session.role
    },
    allRoles(state) {
      const allRoles = []

      for (let [ key, value ] of Object.entries(roles)) {
        allRoles.push({ id: key, ...value })
      }

      return allRoles
    },
    getUserGroups(state) {
      return state.tenantUser.groups || []
    },
    getDisplayName(state) {
      return state.auth.displayName
        ? state.auth.displayName
        : state.auth.email
    },
    getUID(state) {
      return state.auth.uid
    },
    getAuthUser(state) {
      return state.auth
    },
    getUserTenants(state) {
      const userTenants = isObject(state.user.userTenants)
        ? state.user.userTenants
        : {}

      return Object.keys(userTenants)
    },
    isLoggedIn(state, getters) {
      return !!getters.getAuthUser
    },
    isSessionRoleAdminOrOwner(state) {
      return state.session.role === 'admin' || state.session.role === 'owner'
    },
    isSessionRoleOwner(state) {
      return state.session.role === 'owner'
    },
    isEmailVerified(state) {
      return state.auth.emailVerified
    },
    isSessionTenant: (state) => (tenantId) => {
      return state.session.tenant === tenantId
    }
  },
  actions: {
    setSessionDialog({ commit }, val) {
      commit('SET_SESSION_DIALOG', val)
    },
    setReloginDialog({ commit }, val) {
      commit('SET_RELOGIN_DIALOG', val)
    },
    async updateAuthEmail({ commit }, data) {
      const user = this.$firebase.auth().currentUser
      const { email } = data

      if (user && email) {
        await user.updateEmail(email)
          .then(() => {
            commit('setAuthUser')
          })
          .catch(e => {
            console.log(e)
          })
      }
    },
    async updateAuthProfile({ commit }, data) {
      const user = this.$firebase.auth().currentUser
      const { displayName, photoURL } = data

      if (user && (displayName !== undefined || photoURL !== undefined)) {

        const profilePayload = { displayName, photoURL }

        await user.updateProfile(profilePayload)
          .catch(e => console.log(e))

        commit('setAuthUser')
      }
    },
    async changeSession({ dispatch }, tenantId) {
      dispatch('startAppLoading')

      const payload = { tenantId }

      await this.$api.post('/api/auth/session', payload)
        .then(res => {
          dispatch('afterLogin')
          dispatch('resetState')
          this.$router.push({ name: 'admin-home' })
        })
        .catch(e => console.log(e))
        .finally(() => {
          dispatch('stopAppLoading')
        })
    },
    async sendPasswordResetEmail({}, providedEmail) {
      const user = this.$firebase.auth().currentUser
      const { email: userEmail = null } = user || {}
      const email = providedEmail || userEmail

      if (email) {
        await this.$firebase.auth().sendPasswordResetEmail(email)
          .then(() => {
            // Email sent.
            console.log('email sent')
          }).catch((error) => {
            // An error happened.
            console.log(error)
          })
      }
    },
    sendVerifyEmail({}) {
      const user = this.$firebase.auth().currentUser

      const actionCodeSettings = {
        url: `${process.env.VUE_APP_DOMAIN}/admin/finish-verify?email=` + user.email,
        handleCodeInApp: true,
      }

      return user.sendEmailVerification(actionCodeSettings)
        .then(res => {
          return res
        }).catch((error) => {
          return error
        })
    },
    sendSignInLink({}, data) {
      const { email } = data

      if (!email) {
        console.log('No email found')
        return
      }

      const actionCodeSettings = {
        // URL you want to redirect back to. The domain (www.example.com) for this
        // URL must be whitelisted in the Firebase Console.
        url: process.env.VUE_APP_DOMAIN + '/platform/finish-signup',
        // This must be true.
        handleCodeInApp: true,
        //dynamicLinkDomain: 'example.page.link'
      }

      this.$firebase.auth().sendSignInLinkToEmail(email, actionCodeSettings)
        .then(function () {
          // The link was successfully sent. Inform the user.
          // Save the email locally so you don't need to ask the user for it again
          // if they open the link on the same device.
          alert('email sent')

        })
        .catch(function (error) {
          // Some error occurred, you can inspect the code: error.code
          alert(error)
        })
    },
    logout() {
      /**
       * @todo reset state ?
       */
      this.$firebase
        .auth()
        .signOut()
        .then(() => {
          router.go({ path: '/platform/login' })
        })
    },
    async login({ commit, dispatch }, data) {
      const { identity, password, _redirect = true, _sessionSelect = true } = data

      if (isEmailValid(identity)) {
        await this.$firebase.auth()
          .signInWithEmailAndPassword(identity, password)
          .then(res => {
            commit('setAuthUser')

          }, err => dispatch('snackError', err))
      } else {
        dispatch('snackError', { text: 'Invalid email' })
      }
    },
    afterLogin({ dispatch, commit, getters }, data) {
      const { silentLoading = false } = data || {}
      commit('setAuthUser')

      if (!silentLoading) {
        dispatch('promptNotificationPermission')
      }

      const uid = getters.getUID
      let userListener = null

      const onUpdateUser = doc => {
        const user = doc.data()
        commit('setUser', user)

        // All the users tenants are within their root user object
        dispatch('initTenants')
        dispatch('initTenantConfigs')
      }

      const listenerUser = () => {
        userListener = this.$firebase.firestore()
          .collection('users')
          .doc(uid)
          .onSnapshot(onUpdateUser, e => console.log(e))
      }

      const newToken = token => {
        commit('setAuthUser')
        const { role, tenant, account } = token.claims
        const payload = { role, tenant, account }

        if (!role) {
          dispatch('logout')
        }

        commit('setSession', payload)

        listenerUser()

        dispatch('initUnreadAlerts')
      }

      this.$firebase.auth().currentUser
        .getIdTokenResult(/* forceRefresh */ true)
        .then(newToken)
        .catch(e => console.log(e))
    },
    initUser({ commit, dispatch, getters }) {
      const uid = getters.getUID
      const onUpdateUser = doc => {
        const user = doc.data()
        commit('setUser', user)

        // All the users tenants are within their root user object
        dispatch('initTenants')
        dispatch('initTenantConfigs')
      }

      this.$firebase.firestore()
          .collection('users')
          .doc(uid)
          .onSnapshot(onUpdateUser, e => console.log(e))
    }
  },
  mutations: {
    SET_SESSION_DIALOG(state, val) {
      state.sessionDialog = !!val
    },
    SET_RELOGIN_DIALOG(state, val) {
      state.reloginDialog = !!val
    },
    setSession(state, session) {
      const { role, tenant, account } = session
      const struct = { role, tenant, account }
      state.session = { ...struct }
    },
    setUser(state, user) {
      state.user = { ...user }
    },
    setTenantUser(state, tenantUser) {
      state.tenantUser = { ...tenantUser }
    },
    setAuthUser(state) {
      state.auth = this.$firebase.auth().toJSON().currentUser
    },
  },
}
